import {
  featureCurrencies,
  useCurrencyCode,
} from '@aboutyou/legacy-shared/src/utils/currency'
import { usePriceFormatter } from 'helpers/hooks/usePriceFormatter'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC, useCallback } from 'react'
import { useFilterContext } from 'sharedComponents/ProductFilter/FilterProvider'
import { usePriceFilter } from 'sharedComponents/ProductStream/filters/PriceFilter'
import { useMainFilterTracking } from 'sharedComponents/ProductStream/filters/filterTracking'
import { useFeatureFlags } from 'store/context'
import { useFilterStore } from 'store/sidecar/ProductStreamContext'
import styled from 'styled-components'
import DoubleRangeInput from 'uiComponents/atoms/DoubleRangeInput/DoubleRangeInput'
import Toggle from 'uiComponents/atoms/Toggle/Toggle'
import UnfoldingFilterBox from 'uiComponents/organisms/Filters/UnfoldingFilterBox'
import {
  useTrackOnCloseFilter,
  useTrackOnOpenFilter,
} from '../../../../helpers/tracking/hooks/trackFilter'

const StyledToggle = styled(Toggle)`
  padding: 16px 24px;
`

const SALE_FILTER_SLUG = 'sale'

const PriceFilter: FC = () => {
  const { t } = useTranslation()
  const filterStore = useFilterStore()
  const priceFilter = usePriceFilter()
  const { productCurrencyCode } = useFilterContext()
  const priceFormatter = usePriceFormatter({ withoutDecimal: true })

  let currencyCode = useCurrencyCode()
  const { isActive: isFeatureActive } = useFeatureFlags()

  const enableSecondaryLabel = isFeatureActive('enableSecondaryLabel')

  if (enableSecondaryLabel) {
    currencyCode =
      (productCurrencyCode || priceFilter.currencyCode) ===
      featureCurrencies.EUR
        ? featureCurrencies.EUR
        : featureCurrencies.BGN
  }

  const trackFilterChanges = useMainFilterTracking()
  const onCloseFilterTracking = useTrackOnCloseFilter()
  const onOpenFilterTracking = useTrackOnOpenFilter()
  const trackOnClose = () => {
    onCloseFilterTracking('price', 'quick_filters')
  }
  const trackOnOpen = () => {
    onOpenFilterTracking('price', 'quick_filters')
  }

  const hasValidPriceRange =
    priceFilter.availableMinPrice < priceFilter.availableMaxPrice

  const hasValidSelectedPriceRange = priceFilter.range[0] < priceFilter.range[1]

  const handleResetFilters = useCallback(() => {
    priceFilter.reset()
    filterStore.clearFilterGroup(SALE_FILTER_SLUG)
  }, [filterStore, priceFilter])

  if (!hasValidSelectedPriceRange) {
    return null
  }

  const isPriceFilterActive =
    priceFilter.range[0] !== 0 &&
    priceFilter.range[1] !== Infinity &&
    (priceFilter.range[0] !== priceFilter.availableMinPrice ||
      priceFilter.range[1] !== priceFilter.availableMaxPrice)

  const activeFiltersCount =
    (isPriceFilterActive ? 1 : 0) +
    (filterStore.selectedFilters[SALE_FILTER_SLUG]?.length ? 1 : 0)

  return (
    <UnfoldingFilterBox
      label={t('Preis')}
      dataTestId={'Price'}
      activeFiltersCount={activeFiltersCount}
      onReset={handleResetFilters}
      styledContentStyles={{ padding: '24px' }}
      onClose={trackOnClose}
      onOpen={trackOnOpen}
      settings={[
        <StyledToggle
          label={t('Nur Artikel im Sale anzeigen')}
          checked={Boolean(
            filterStore.selectedFilters[SALE_FILTER_SLUG]?.length,
          )}
          onChange={() => filterStore.toggleSelected(SALE_FILTER_SLUG)}
          key="sale-filter-toggle"
          data-testid={`Toggle-${SALE_FILTER_SLUG}`}
        />,
      ]}
    >
      {hasValidPriceRange && (
        <DoubleRangeInput
          scale={
            priceFilter.availableMaxPrice - priceFilter.availableMinPrice <
            100000
              ? 'linear'
              : 'log'
          }
          values={priceFilter.range}
          onChange={ev => {
            priceFilter.updateIntermediateRange(ev.values)
          }}
          onApply={ev => {
            if (!ev.adjustRange) {
              priceFilter.updateRange({
                min: String(ev.values[0]),
                max: String(ev.values[1]),
              })
              trackFilterChanges('prices')
            }
          }}
          min={priceFilter.availableMinPrice}
          max={priceFilter.availableMaxPrice}
          transformLabel={(value: number) =>
            priceFormatter(Math.floor(value / 100), currencyCode)
          }
        />
      )}
    </UnfoldingFilterBox>
  )
}

export default PriceFilter
